import Image from 'next/image';
import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import { ArrowGlyph } from '@/components/agents/primitives';

import { useEmployeesT, useHireHref } from './kit';

const SUNRISE =
  'https://media.easy-peasy.ai/landing/ai-employees/v1/sunrise-1920.webp';
const SUNRISE_SMALL =
  'https://media.easy-peasy.ai/landing/ai-employees/v1/sunrise-1080.webp';

/** The closing call to action, on the misty sunrise of the film's end card. */
export default function EmployeesFinalCta({ faces }: { faces: string[] }) {
  const { t } = useEmployeesT();
  const hireHref = useHireHref();

  return (
    <section className='mx-auto max-w-7xl px-3 pb-24 pt-8 sm:px-6 sm:pb-32 lg:px-8'>
      <div className='relative isolate overflow-hidden rounded-[28px] bg-[#efe6e3] ring-1 ring-inset ring-black/[0.04] sm:rounded-[36px]'>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={SUNRISE}
          srcSet={`${SUNRISE_SMALL} 1080w, ${SUNRISE} 1920w`}
          sizes='(min-width: 1280px) 1216px, 100vw'
          alt=''
          loading='lazy'
          decoding='async'
          className='absolute inset-0 -z-10 h-full w-full object-cover object-[50%_60%]'
        />
        <div className='absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.55),rgba(255,255,255,0)_70%)]' />

        <div className='px-6 py-20 text-center sm:px-14 sm:py-28'>
          <div
            aria-hidden='true'
            className='flex justify-center -space-x-3 rtl:space-x-reverse'
          >
            {faces.map((face, index) => (
              <Image
                key={face}
                src={face}
                alt=''
                width={56}
                height={56}
                unoptimized
                className={clsxm(
                  'h-11 w-11 rounded-full object-cover ring-[3px] ring-white sm:h-14 sm:w-14',
                  index >= 5 && 'hidden sm:block',
                )}
              />
            ))}
          </div>
          <h2
            className={clsxm(
              styles.display,
              'mx-auto mt-9 max-w-3xl text-[2.5rem] font-semibold leading-[1.04] text-[#10101c] sm:text-6xl lg:text-[4.5rem]',
            )}
          >
            {t('Hire your AI team.')}
          </h2>
          <p className='mx-auto mt-6 max-w-xl text-lg leading-8 text-[#3d3d52]'>
            {t('Free to start. Your first employee is ready in a minute.')}
          </p>
          <div className='mt-10 flex flex-wrap items-center justify-center gap-3'>
            <Link
              href={hireHref}
              className='inline-flex items-center justify-center gap-2 rounded-full bg-[#10101c] px-6 py-3.5 text-[15px] font-semibold text-white shadow-[0_14px_30px_-12px_rgba(16,16,28,0.6)] transition-colors duration-200 hover:bg-[#26263a] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#10101c]'
            >
              {t('Hire your team free')}
              <ArrowGlyph />
            </Link>
            <a
              href='#pricing'
              className='inline-flex items-center justify-center gap-2 rounded-full bg-white/70 px-6 py-3.5 text-[15px] font-semibold text-[#10101c] ring-1 ring-inset ring-black/10 transition duration-200 hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#10101c]'
            >
              {t('See plans')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
